import { useHistory } from 'react-router-dom';

import { BOI_WALLET_USER_STORAGE } from '../constants';
import { basePath } from '../constants/commonConstants';
import { routes } from '../routes';
import { getItemFromSessionStorage } from './sessionStorageHelper';
import { getAccessToken } from './UserAccessHelper';

const formatPayload = payload => {
  return payload ? { body: JSON.stringify(payload) } : {};
};

const setAuthorizationToken = authToken => {
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
};

const redirectToLoginPage = (history = {}) => {
  if (typeof history.push === 'function') {
    history.push(routes.loginPage.url);
  }
};

const redirectToHomePageWithCacheRefresh = () => {
  const baseUrl = `${process.env.PUBLIC_URL}/`;
  window.location.replace(baseUrl);
};

const checkResponseForBody = (response, onError) => {
  try {
    const res = response.json();
    return res.then(
      res => res,
      error => {
        if (onError) {
          onError();
        }
        return { error: true, errorMessage: error.message };
      }
    );
  } catch (error) {
    if (onError) {
      onError();
    }
    return new Promise(res =>
      res({ error: true, errorMessage: error.message })
    );
  }
};

export const getRequestBuilder = async (url, params, onError) => {
  const requestParams = params && Object.keys(params).length > 0 ?
    '?' + new URLSearchParams(params).toString()
    : '';

  const requestUrl = basePath + url + requestParams;
  const cognitoAccessTokenKey = getAccessToken(
    JSON.parse(getItemFromSessionStorage(BOI_WALLET_USER_STORAGE))
  );
  const authToken = getItemFromSessionStorage(cognitoAccessTokenKey) || '';
  const settings = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...setAuthorizationToken(authToken),
      'x-custom-ui-version': 'release/1.28.2/72'
    }
  };
  return fetch(requestUrl, settings).then(
    response => {
      switch (response.status) {
        case 412:
          // response 412 means there is a mismatch between ui version and backend version
          redirectToHomePageWithCacheRefresh();
          break;
        case 401:
          // The user got an unauthorized call and therefore needs login
          useHistory().push(routes.loginPage.url);
          break;
        case 504:
          return { ...response, error: true };
        default:
          return checkResponseForBody(response, onError);
      }
    },
    error => {
      if (onError) {
        onError();
      }
      return new Promise(res =>
        res({ error: true, errorMessage: error.message })
      );
    }
  )
}

export const makeRequest = async ({
  url,
  method,
  payload,
  onError,
  settingsOverride = {},
  history = {}
}) => {
  const cognitoAccessTokenKey = getAccessToken(
    JSON.parse(getItemFromSessionStorage(BOI_WALLET_USER_STORAGE))
  );
  const authToken = getItemFromSessionStorage(cognitoAccessTokenKey) || '';
  const settings = {
    method: method,
    headers: {
      Accept: 'application/json',
      ...setAuthorizationToken(authToken),
      'Content-Type': 'application/json',
      'x-custom-ui-version': 'release/1.28.2/72'
    },
    ...formatPayload(payload)
  };
  const finalSettings = { ...settings, ...settingsOverride };
  return fetch(url, finalSettings).then(
    response => {
      switch (response.status) {
        case 412:
          // response 412 means there is a mismatch between ui version and backend version
          redirectToHomePageWithCacheRefresh();
          break;
        case 401:
          // The user got an unauthorized call and therefore needs login
          redirectToLoginPage(history);
          break;
        case 504:
          return { ...response, error: true };
        default:
          return checkResponseForBody(response, onError);
      }
    },
    error => {
      if (onError) {
        onError();
      }
      return new Promise(res =>
        res({ error: true, errorMessage: error.message })
      );
    }
  );
};

// eslint-disable-next-line complexity
export const sendAnalyticsEvent = async (quotePromise, event, label) => {
  if (quotePromise && !quotePromise.error) {
    let quote;

    Array.isArray(quotePromise)
      ? (quote = quotePromise[0])
      : (quote = quotePromise);

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: `${event}`,
      eventLabel: `${label} Insurance quote`,
      quoteNum: `${quote.quote_reference}`
    });
  }
};
